/**
 * Image generation hook for the social media composer.
 *
 * Wraps the image-generator arche's API endpoints (generate, poll, cancel)
 * into a flow suitable for the media picker modal. Supports single and
 * multi-variant generation, prompt enhancement, advanced settings,
 * and reference images.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import api, { ImageModel, ImageProviderInfo } from '@/lib/api';
import { socialMediaApi } from '../api';
import { useStreaming } from '@/lib/hooks';
import { authApi } from '@/lib/auth-api';

const ARCHE_ID = 'arche_image_generator';
const WEBSOCKET_ENDPOINT = import.meta.env.VITE_WEBSOCKET_ENDPOINT || '';
const MAX_POLL_ATTEMPTS = 150;

// ─────────────────────────────────────────────────────────────────────────────
// Style presets — curated list for social media use cases
// ─────────────────────────────────────────────────────────────────────────────

export interface ImageStylePreset {
  id: string;
  label: string;
  description: string;
  /** Extra prompt text appended automatically */
  promptSuffix: string;
  /** Provider-level style hint (when supported) */
  style?: string;
}

export const IMAGE_STYLE_PRESETS: ImageStylePreset[] = [
  {
    id: 'realistic',
    label: 'Realistic',
    description: 'Photorealistic, natural lighting',
    promptSuffix: 'photorealistic, natural lighting, highly detailed photograph',
    style: 'natural',
  },
  {
    id: 'vivid',
    label: 'Vivid',
    description: 'Bold colors, hyper-saturated',
    promptSuffix: 'vivid colors, hyper-saturated, dramatic lighting, eye-catching',
    style: 'vivid',
  },
  {
    id: 'pop-art',
    label: 'Pop Art',
    description: 'Bold outlines, halftone dots, bright palette',
    promptSuffix:
      'pop art style, bold outlines, halftone dots, bright primary colors, Andy Warhol inspired',
  },
  {
    id: 'ghibli',
    label: 'Studio Ghibli',
    description: 'Soft watercolor anime aesthetic',
    promptSuffix: 'Studio Ghibli style, soft watercolor, anime aesthetic, lush scenery, warm tones',
  },
  {
    id: 'minimalist',
    label: 'Minimalist',
    description: 'Clean, simple, lots of white space',
    promptSuffix:
      'minimalist design, clean composition, white space, simple shapes, modern aesthetic',
  },
  {
    id: 'watercolor',
    label: 'Watercolor',
    description: 'Soft washes, artistic brushstrokes',
    promptSuffix:
      'watercolor painting, soft washes, artistic brushstrokes, paper texture, delicate colors',
  },
  {
    id: 'cinematic',
    label: 'Cinematic',
    description: 'Movie still, dramatic composition',
    promptSuffix: 'cinematic shot, dramatic composition, film grain, anamorphic lens, movie still',
  },
  {
    id: 'flat-illustration',
    label: 'Flat Illustration',
    description: 'Vector-style, social media friendly',
    promptSuffix:
      'flat illustration, vector art style, clean lines, modern digital illustration, social media graphic',
  },
  {
    id: '3d-render',
    label: '3D Render',
    description: 'Glossy 3D, rendered scene',
    promptSuffix: '3D render, glossy materials, studio lighting, octane render, blender style',
  },
  {
    id: 'none',
    label: 'No Style',
    description: 'Let the AI decide',
    promptSuffix: '',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Size options — social-media-friendly aspect ratios
// ─────────────────────────────────────────────────────────────────────────────

export const IMAGE_SIZE_OPTIONS = [
  { value: '1:1', label: 'Square (1:1)', hint: 'Instagram, Facebook' },
  { value: '16:9', label: 'Landscape (16:9)', hint: 'Twitter/X, YouTube' },
  { value: '9:16', label: 'Portrait (9:16)', hint: 'Stories, Reels, TikTok' },
  { value: '4:3', label: 'Landscape (4:3)', hint: 'Facebook, general' },
  { value: '3:4', label: 'Portrait (3:4)', hint: 'Pinterest' },
  { value: '3:2', label: 'Landscape (3:2)', hint: 'Photography' },
  { value: '2:3', label: 'Portrait (2:3)', hint: 'Photography' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Style chips — exhaustive list organized by category
// ─────────────────────────────────────────────────────────────────────────────

export interface StyleChipCategory {
  label: string;
  chips: string[];
}

export const STYLE_CHIP_CATEGORIES: StyleChipCategory[] = [
  {
    label: 'Photographic',
    chips: [
      'Photorealistic',
      'Editorial photography',
      'Film grain',
      'Cinematic',
      'Portrait',
      'Documentary',
      'Product photography',
      'Aerial / drone shot',
    ],
  },
  {
    label: 'Illustrated',
    chips: [
      'Illustration',
      'Flat design',
      'Line art / sketch',
      'Watercolor',
      'Oil painting',
      'Gouache',
      'Ink wash',
      'Woodblock print',
    ],
  },
  {
    label: 'Digital / 3D',
    chips: [
      '3D render',
      'CGI',
      'Isometric',
      'Low-poly',
      'Pixel art',
      'Neon / cyberpunk',
      'Vaporwave',
      'Glassmorphism',
    ],
  },
  {
    label: 'Art historical / stylized',
    chips: [
      'Impressionist',
      'Art Nouveau',
      'Bauhaus',
      'Vintage / retro',
      'Pop art',
      'Minimalist',
      'Surrealist',
    ],
  },
  {
    label: 'Anime / illustrated fiction',
    chips: ['Anime / manga', 'Studio Ghibli style', 'Comic book', 'Storybook', 'Concept art'],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Hook types
// ─────────────────────────────────────────────────────────────────────────────

export type GenerationStage = 'idle' | 'starting' | 'generating' | 'storing' | 'complete' | 'error';

export interface GeneratedImageResult {
  requestId: string;
  imageRef: string;
  imageUrl: string;
  /** The file ID of the auto-created user_file record */
  fileId?: string;
  prompt: string;
  revisedPrompt?: string;
  /** Which model generated this image */
  model?: string;
  /** Which provider generated this image */
  provider?: string;
  /** Generation time in milliseconds */
  generationTimeMs?: number;
  /** Variant slot index (0-based) when generating multiple variants */
  slotIndex?: number;
}

/**
 * A single field in the structured prompt.
 * Each has a label (displayed in the UI) and a value (the descriptive text).
 */
export interface PromptField {
  label: string;
  value: string;
}

/**
 * The structured prompt produced by AI enhancement.
 * Each field is an independently editable component.
 */
export interface EnhancedPromptResult {
  fields: PromptField[];
  clarification?: string;
}

/** Default field labels offered by the AI enhancement endpoint */
export const ENHANCED_PROMPT_LABELS = [
  'Subject',
  'Foreground',
  'Background',
  'Lighting',
  'Mood',
  'Style',
  'Color palette',
  'Composition',
  'Details',
] as const;

export interface GenerateOptions {
  prompt: string;
  /** Structured prompt fields from enhancement or manual entry */
  enhancedFields?: PromptField[];
  stylePresetId?: string;
  /** Free-text style (from chips + manual input) */
  styleText?: string;
  size?: string;
  model?: string;
  provider?: string;
  /** Number of variants to generate (1-4) */
  variantCount?: number;
  /** Quality level */
  quality?: 'standard' | 'hd' | 'ultra-hd';
  /** Negative prompt — things to avoid */
  negativePrompt?: string;
  /** Color mood hint */
  colorMood?: string;
  /** Seed for reproducibility */
  seed?: number;
  /** Raw mode — bypass style enhancements */
  rawMode?: boolean;
  /** Reference image file ID for style transfer */
  referenceImageFileId?: string;
  /** Reference image strength (0-100) */
  referenceStrength?: number;
}

export interface UseImageGenerationReturn {
  /** Current generation stage */
  stage: GenerationStage;
  /** Human-readable progress message */
  progressMessage: string;
  /** Error message if stage === 'error' */
  error: string | null;
  /** The most recently generated image (available when stage === 'complete') */
  result: GeneratedImageResult | null;
  /** All results from the current batch (for multi-variant) */
  results: GeneratedImageResult[];
  /** History of all images generated in this session */
  history: GeneratedImageResult[];
  /** Available image models from the backend */
  models: ImageModel[];
  /** Available providers */
  providers: ImageProviderInfo[];
  /** Whether models are loading */
  modelsLoading: boolean;
  /** Variant generation progress */
  variantProgress: { current: number; total: number } | null;
  /** Start a new generation (supports multi-variant) */
  generate: (opts: GenerateOptions) => Promise<void>;
  /** Cancel the current generation */
  cancel: () => void;
  /** Reset state (clear results, go back to idle) */
  reset: () => void;
  /** Enhance a raw prompt using AI */
  enhancePrompt: (rawPrompt: string) => Promise<EnhancedPromptResult>;
  /** Whether prompt enhancement is in progress */
  isEnhancing: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Structured prompt builder
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds a structured prompt from the user's original text, enhanced fields,
 * style settings, and other options. Outputs a readable block format:
 *
 * ```
 * Original Text: "user's original prompt"
 * Subject: "expanded subject"
 * Background: "..."
 * ...
 *
 * Style Type:
 * - cinematic
 * - watercolor
 * ```
 */
function buildStructuredPrompt(originalText: string, opts: GenerateOptions): string {
  if (opts.rawMode) return originalText;

  const lines: string[] = [];

  // Original text always comes first
  lines.push(`Original Text: "${originalText}"`);

  // Enhanced fields (from AI or manual)
  if (opts.enhancedFields && opts.enhancedFields.length > 0) {
    for (const field of opts.enhancedFields) {
      if (field.value.trim()) {
        lines.push(`${field.label}: "${field.value.trim()}"`);
      }
    }
  }

  // Color mood
  if (opts.colorMood && opts.colorMood !== 'auto') {
    lines.push(`Color Palette: "${opts.colorMood}"`);
  }

  // Style section
  const styleEntries: string[] = [];

  // From preset
  const preset = IMAGE_STYLE_PRESETS.find((p) => p.id === opts.stylePresetId);
  if (preset?.promptSuffix) {
    styleEntries.push(preset.label);
  }

  // From chips + manual text
  if (opts.styleText?.trim()) {
    const chips = opts.styleText
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    for (const chip of chips) {
      if (!styleEntries.includes(chip)) {
        styleEntries.push(chip);
      }
    }
  }

  if (styleEntries.length > 0) {
    lines.push('');
    lines.push('Style Type:');
    for (const entry of styleEntries) {
      lines.push(`- ${entry}`);
    }
  }

  // Negative prompt
  if (opts.negativePrompt?.trim()) {
    lines.push('');
    lines.push(`Avoid: "${opts.negativePrompt.trim()}"`);
  }

  const structured = lines.join('\n');

  console.log('[ImageGeneration] Structured prompt:\n' + structured);

  return structured;
}

export function useImageGeneration(): UseImageGenerationReturn {
  const [stage, setStage] = useState<GenerationStage>('idle');
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GeneratedImageResult | null>(null);
  const [results, setResults] = useState<GeneratedImageResult[]>([]);
  const [history, setHistory] = useState<GeneratedImageResult[]>([]);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [variantProgress, setVariantProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [isEnhancing, setIsEnhancing] = useState(false);

  // Models
  const [models, setModels] = useState<ImageModel[]>([]);
  const [providers, setProviders] = useState<ImageProviderInfo[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);

  // Track prompt & generation options for the current request
  const currentPromptRef = useRef('');
  const pollAttemptsRef = useRef(0);
  const pendingVariantsRef = useRef<GenerateOptions[]>([]);
  const batchResultsRef = useRef<GeneratedImageResult[]>([]);
  const generationStartRef = useRef<number>(0);

  // Load available models on mount
  useEffect(() => {
    let cancelled = false;
    setModelsLoading(true);
    api
      .listImageModels(ARCHE_ID)
      .then((resp) => {
        if (!cancelled) {
          setModels(resp.models);
          setProviders(resp.providers);
        }
      })
      .catch(() => {
        // Non-fatal — user can still generate with defaults
      })
      .finally(() => {
        if (!cancelled) setModelsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ─── WebSocket streaming ────────────────────────────────────────────────
  const getAuthToken = useCallback(() => authApi.getIdToken(), []);

  const handleProgress = useCallback((_stage: string, message: string) => {
    setStage(_stage as GenerationStage);
    setProgressMessage(message);
  }, []);

  const handleImageComplete = useCallback(
    (data: {
      requestId: string;
      imageRef: string;
      fileId?: string;
      revisedPrompt?: string;
      provider: string;
      model: string;
    }) => {
      const elapsed = Date.now() - generationStartRef.current;
      api
        .getImageUrl(ARCHE_ID, data.imageRef)
        .then((url) => {
          const img: GeneratedImageResult = {
            requestId: data.requestId,
            imageRef: data.imageRef,
            imageUrl: url,
            fileId: data.fileId,
            prompt: currentPromptRef.current,
            revisedPrompt: data.revisedPrompt,
            model: data.model,
            provider: data.provider,
            generationTimeMs: elapsed,
            slotIndex: batchResultsRef.current.length,
          };

          batchResultsRef.current = [...batchResultsRef.current, img];
          setResults([...batchResultsRef.current]);
          setResult(img);
          setHistory((prev) => [img, ...prev]);

          // Check if more variants need to be generated
          if (pendingVariantsRef.current.length > 0) {
            const nextOpts = pendingVariantsRef.current.shift()!;
            setVariantProgress((prev) => (prev ? { ...prev, current: prev.current + 1 } : null));
            void fireGeneration(nextOpts);
          } else {
            setStage('complete');
            setProgressMessage('Image generated!');
            setVariantProgress(null);
            setRequestId(null);
          }
        })
        .catch(() => {
          setStage('error');
          setError('Generated image but failed to load preview');
        });
    },
    [],
  );

  const handleStreamingError = useCallback((_code: string, message: string) => {
    // If we have some results already, show what we have
    if (batchResultsRef.current.length > 0) {
      setStage('complete');
      setProgressMessage(`Completed ${batchResultsRef.current.length} variant(s). Some failed.`);
      setError(message || 'Some image generations failed');
    } else {
      setStage('error');
      setError(message || 'Image generation failed');
    }
    setRequestId(null);
    setVariantProgress(null);
    pendingVariantsRef.current = [];
  }, []);

  useStreaming({
    requestId,
    authToken: getAuthToken(),
    endpoint: WEBSOCKET_ENDPOINT,
    enabled: !!requestId,
    onProgress: handleProgress,
    onImageComplete: handleImageComplete,
    onError: handleStreamingError,
  });

  // ─── Polling fallback ──────────────────────────────────────────────────
  useEffect(() => {
    if (!requestId || stage === 'complete' || stage === 'error' || stage === 'idle') return;

    pollAttemptsRef.current = 0;
    const interval = setInterval(async () => {
      if (!requestId) return;
      pollAttemptsRef.current += 1;

      if (pollAttemptsRef.current > MAX_POLL_ATTEMPTS) {
        if (batchResultsRef.current.length > 0) {
          setStage('complete');
          setProgressMessage(
            `Completed ${batchResultsRef.current.length} variant(s). Some timed out.`,
          );
        } else {
          setStage('error');
          setError('Image generation timed out. Please try again.');
        }
        setRequestId(null);
        setVariantProgress(null);
        pendingVariantsRef.current = [];
        return;
      }

      try {
        const status = await api.checkImageStatus(ARCHE_ID, requestId);
        if (status.status === 'error') {
          if (batchResultsRef.current.length > 0) {
            setStage('complete');
            setError(status.error || 'Some variants failed');
          } else {
            setStage('error');
            setError(status.error || 'Image generation failed');
          }
          setRequestId(null);
          setVariantProgress(null);
          pendingVariantsRef.current = [];
        } else if (status.status === 'complete' && status.url && status.imageRef) {
          const elapsed = Date.now() - generationStartRef.current;
          const img: GeneratedImageResult = {
            requestId,
            imageRef: status.imageRef,
            imageUrl: status.url,
            fileId: status.fileId,
            prompt: currentPromptRef.current,
            generationTimeMs: elapsed,
            slotIndex: batchResultsRef.current.length,
          };

          batchResultsRef.current = [...batchResultsRef.current, img];
          setResults([...batchResultsRef.current]);
          setResult(img);
          setHistory((prev) => [img, ...prev]);

          if (pendingVariantsRef.current.length > 0) {
            const nextOpts = pendingVariantsRef.current.shift()!;
            setVariantProgress((prev) => (prev ? { ...prev, current: prev.current + 1 } : null));
            void fireGeneration(nextOpts);
          } else {
            setStage('complete');
            setProgressMessage('Image generated!');
            setRequestId(null);
            setVariantProgress(null);
          }
        }
      } catch {
        // Polling failure is non-fatal — WebSocket may still deliver
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [requestId, stage]);

  // ─── Internal: fire a single generation request ────────────────────────
  const fireGeneration = useCallback(
    async (opts: GenerateOptions) => {
      const trimmed = opts.prompt.trim();
      if (!trimmed) return;

      generationStartRef.current = Date.now();

      // Build structured prompt
      const fullPrompt = buildStructuredPrompt(trimmed, opts);
      currentPromptRef.current = trimmed;

      // Pick model
      let selectedModel = opts.model;
      let selectedProvider = opts.provider;

      if (!selectedModel && providers.length > 0) {
        const recommended = providers[0];
        const recModel = models.find((m) => m.id === recommended.recommendedModelId);
        selectedModel = recModel?.id ?? models[0]?.id;
        selectedProvider = recModel?.provider ?? models[0]?.provider;
      }

      const preset = IMAGE_STYLE_PRESETS.find((p) => p.id === opts.stylePresetId);

      const generationPayload = {
        prompt: fullPrompt,
        model: selectedModel,
        provider: selectedProvider,
        size: opts.size || '1:1',
        quality: opts.quality || 'standard',
        style: preset?.style,
        referenceImageFileId: opts.referenceImageFileId,
        referenceStrength:
          opts.referenceStrength != null ? opts.referenceStrength / 100 : undefined,
        providerParams: {
          ...(opts.negativePrompt ? { negative_prompt: opts.negativePrompt } : {}),
          ...(opts.seed != null ? { seed: opts.seed } : {}),
          ...(opts.rawMode ? { raw_mode: true } : {}),
        },
      };

      console.log('[ImageGeneration] Sending request:', generationPayload);

      try {
        const response = await api.generateImage(ARCHE_ID, generationPayload);

        setRequestId(response.requestId);
        setStage('generating');
        const total = variantProgress?.total ?? 1;
        const current = batchResultsRef.current.length + 1;
        setProgressMessage(
          total > 1
            ? `Generating variant ${current} of ${total}...`
            : 'AI is creating your image...',
        );
      } catch (err) {
        if (batchResultsRef.current.length > 0) {
          setStage('complete');
          setError(
            `Generated ${batchResultsRef.current.length} variant(s) but some failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
          );
        } else {
          setStage('error');
          setError(err instanceof Error ? err.message : 'Failed to start image generation');
        }
        setVariantProgress(null);
        pendingVariantsRef.current = [];
      }
    },
    [models, providers, variantProgress],
  );

  // ─── Public API ────────────────────────────────────────────────────────

  const generate = useCallback(
    async (opts: GenerateOptions) => {
      const trimmed = opts.prompt.trim();
      if (!trimmed) return;

      const variantCount = Math.min(Math.max(opts.variantCount ?? 1, 1), 4);

      setStage('starting');
      setProgressMessage('Starting image generation...');
      setError(null);
      setResult(null);
      setResults([]);
      batchResultsRef.current = [];
      pendingVariantsRef.current = [];

      if (variantCount > 1) {
        setVariantProgress({ current: 1, total: variantCount });
        // Queue remaining variants (the first one fires immediately)
        for (let i = 1; i < variantCount; i++) {
          pendingVariantsRef.current.push({ ...opts, variantCount: 1 });
        }
      }

      await fireGeneration(opts);
    },
    [fireGeneration],
  );

  const cancel = useCallback(() => {
    if (requestId) {
      api.cancelImageGeneration(ARCHE_ID, requestId).catch(() => {});
      setRequestId(null);
    }
    pendingVariantsRef.current = [];
    setVariantProgress(null);

    // If we have partial results, show them
    if (batchResultsRef.current.length > 0) {
      setStage('complete');
      setProgressMessage(`Cancelled. ${batchResultsRef.current.length} variant(s) completed.`);
    } else {
      setStage('idle');
      setProgressMessage('');
    }
    setError(null);
  }, [requestId]);

  const reset = useCallback(() => {
    setStage('idle');
    setProgressMessage('');
    setError(null);
    setResult(null);
    setResults([]);
    setVariantProgress(null);
    batchResultsRef.current = [];
    pendingVariantsRef.current = [];
  }, []);

  const enhancePrompt = useCallback(async (rawPrompt: string): Promise<EnhancedPromptResult> => {
    setIsEnhancing(true);
    try {
      const response = await socialMediaApi.enhanceImagePrompt(rawPrompt);
      return response.enhanced;
    } catch (err) {
      throw new Error(err instanceof Error ? err.message : 'Failed to enhance prompt');
    } finally {
      setIsEnhancing(false);
    }
  }, []);

  return {
    stage,
    progressMessage,
    error,
    result,
    results,
    history,
    models,
    providers,
    modelsLoading,
    variantProgress,
    generate,
    cancel,
    reset,
    enhancePrompt,
    isEnhancing,
  };
}
